import { EditorNodeName } from "@/enums/customAttributes";
import { SERVICE_CUSTOM } from "@/helpers/constants";
import { stripColonPrefix } from "@/helpers/parser";

import type { EditorNode } from "@/models/TextEditor";
import type { FormulaOption } from "@/models/options";
import type { IAttribute } from "@/models/orchestration";

export const tokenArrayToString = (
  tokens: EditorNode[] | null | undefined
): string => {
  if (!tokens?.length) {
    return "";
  }

  const mappedTokens = tokens.map((token) => {
    if (token.type === EditorNodeName.whitespace) {
      return token.content;
    }

    if (token.type === EditorNodeName.attribute && token.attributeId) {
      const attrName = token.attributeId;
      const prefix = attrName.startsWith(":") ? "" : ":";
      const contextStr =
        token.context && "value" in token.context && token.context.value
          ? JSON.stringify(token.context)
          : "";
      return `${prefix}${attrName}${contextStr}`;
    }

    if (token.type === EditorNodeName.regex) {
      const hasQuotes =
        token.content?.startsWith('"') || token.content?.startsWith("'");

      return hasQuotes ? token.content : `"${token.content}"`;
    }

    return token.content;
  });

  return mappedTokens.join("");
};

export const validateTokenArray = (
  tokens: EditorNode[],
  functionsByNameMap: Record<string, FormulaOption>,
  serviceAttributes: IAttribute[]
): string[] => {
  const errors: string[] = [];

  for (const token of tokens) {
    if (token.type === EditorNodeName.operator) {
      const operatorDef = functionsByNameMap[token.content.toUpperCase()];
      if (!operatorDef) {
        errors.push(`Unknown operator: ${token.content}`);
      }
    }

    if (token.type === EditorNodeName.function) {
      const functionDef = functionsByNameMap[token.content.toUpperCase()];
      if (!functionDef) {
        errors.push(`Unknown function: ${token.content}`);
      }
    }

    if (token.type === EditorNodeName.attribute) {
      if (token.attributeId === SERVICE_CUSTOM) {
        continue;
      }
      const cleanName = stripColonPrefix(token.content ?? "");
      const attributeExists = serviceAttributes.some(
        (attr) => attr.name === cleanName || attr.id === cleanName
      );
      if (!attributeExists) {
        errors.push(`Unknown attribute: ${token.content}`);
      }
    }
  }

  return errors;
};

export const createEmptyEditorNode = (): EditorNode => {
  return {
    id: "",
    uiId: "0:empty",
    type: EditorNodeName.uncategorized,
    content: "",
    atomic: false,
    isOperand: false,
    isOperator: false,
  };
};
